import React, { useState, useContext, useEffect } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [authMethod, setAuthMethod] = useState(null);
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const showErrorToast = (message) => {
    toast.error(message, {
      style: {
        background: '#fee2e2',
        border: '1px solid #dc2626',
        color: '#7f1d1d'
      }
    });
  };
  
  // Get redirect params from URL
  const redirectPath = searchParams.get('redirect');
  const shouldRegister = searchParams.get('register') === 'true';

  const handleEmailCheck = async (e) => {
    e.preventDefault();
    if (!email) {
      showErrorToast('Inserisci la tua email');
      return;
    }

    setCheckingEmail(true);
    try {
      const response = await axios.post('/auth/check-method', { email });
      
      if (!response.data.exists) {
        showErrorToast('Utente non trovato. Contatta l\'amministrazione per registrarti.');
        setCheckingEmail(false);
        return;
      }

      setAuthMethod(response.data.auth_method);
      
      // If OTP email, send OTP automatically
      if (response.data.auth_method === 'otp_email') {
        await handleSendOTP();
      }
    } catch (error) {
      showErrorToast('Errore nella verifica email');
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSendOTP = async () => {
    try {
      await axios.post('/otp/send-email', { 
        contact: email, 
        contact_type: 'email',
        purpose: 'login'
      });
      setOtpSent(true);
      toast.success('Codice OTP inviato alla tua email!');
    } catch (error) {
      showErrorToast('Errore nell\'invio del codice OTP');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loginPayload = { email };
      
      if (authMethod === 'password') {
        if (!password) {
          showErrorToast('Inserisci la password');
          setLoading(false);
          return;
        }
        loginPayload.password = password;
      } else if (authMethod === 'otp_email') {
        if (!otpCode) {
          showErrorToast('Inserisci il codice OTP');
          setLoading(false);
          return;
        }
        
        // First verify OTP
        await axios.post('/otp/verify-email', {
          contact: email,
          code: otpCode,
          contact_type: 'email'
        });
        
        loginPayload.otp_code = otpCode;
      }

      const response = await axios.post('/auth/login', loginPayload);
      await login(response.data.token, response.data.user);
      toast.success('Login effettuato con successo!');
      
      // If coming from event registration, redirect there
      if (redirectPath && shouldRegister) {
        navigate(redirectPath);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Credenziali non valide');
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEmail('');
    setAuthMethod(null);
    setPassword('');
    setOtpCode('');
    setOtpSent(false);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Link to="/" className="text-3xl font-bold" data-testid="logo-link">BASE</Link>
          <p className="text-gray-600 mt-2">Accedi alla tua area riservata</p>
        </div>

        <div className="space-y-6">
          {/* Step 1: Email input */}
          {!authMethod ? (
            <form onSubmit={handleEmailCheck} className="space-y-6" data-testid="email-check-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="inserisci@email.it"
                />
              </div>
              <Button
                type="submit"
                disabled={checkingEmail}
                className="w-full bg-black text-white hover:bg-gray-800"
                data-testid="email-check-button"
              >
                {checkingEmail ? 'Verifica...' : 'Continua'}
              </Button>
            </form>
          ) : (
            /* Step 2: Password or OTP input */
            <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
              <div>
                <Label>Email</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={email}
                    disabled
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleReset}
                    className="border-2 border-black hover:bg-black hover:text-white"
                    data-testid="change-email-button"
                  >
                    Cambia
                  </Button>
                </div>
              </div>

              {authMethod === 'password' ? (
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Inserisci la tua password"
                    autoFocus
                  />
                </div>
              ) : (
                <div>
                  <Label htmlFor="otp">Codice OTP</Label>
                  <Input
                    id="otp"
                    data-testid="login-otp-input"
                    type="text"
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="Inserisci il codice a 6 cifre"
                    maxLength={6}
                    autoFocus
                  />
                  {otpSent && (
                    <p className="text-sm text-gray-600 mt-2">
                      Codice inviato alla tua email. Controlla anche lo spam.
                    </p>
                  )}
                  <Button
                    type="button"
                    variant="link"
                    onClick={handleSendOTP}
                    className="mt-2 p-0 text-sm"
                    disabled={loading}
                    data-testid="resend-otp-button"
                  >
                    Invia di nuovo il codice
                  </Button>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white hover:bg-gray-800"
                data-testid="login-submit-button"
              >
                {loading ? 'Accesso...' : 'Accedi'}
              </Button>
            </form>
          )}
        </div>

        <div className="text-center mt-8">
          <Link to="/" className="text-gray-600 hover:text-black" data-testid="back-home-link">
            ← Torna alla Home
          </Link>
        </div>
      </div>
    </div>
  );
}
